import DOMPurify from "dompurify";

/**
 * The tag set Baqaal's own sanitizer allows
 * (`vendor-terms/utils/terms-html.sanitizer.ts`). Kept identical here, not
 * widened: the server already snapshots each version through this list, so
 * anything outside it means the server contract changed and we would rather
 * drop it than render it.
 */
const ALLOWED_TAGS = [
	"h1",
	"h2",
	"h3",
	"h4",
	"p",
	"ul",
	"ol",
	"li",
	"strong",
	"em",
	"u",
	"br",
	"a",
	"table",
	"thead",
	"tbody",
	"tr",
	"th",
	"td",
	"blockquote",
];

/**
 * `href` and `start` — no styles, classes, ids or data attributes. The portal
 * owns presentation.
 *
 * `start` is structural, not presentational: the CMS marks each clause up as
 * its own single-item list carrying the clause number (`<ol start="13">` for
 * clause 13), so dropping it renumbers all twenty-odd clauses to "1." and the
 * document's own cross-references stop resolving. It is a numeric attribute on
 * `<ol>` with no URL or script surface.
 */
const ALLOWED_ATTR = ["href", "start"];

/**
 * Second line of defence over server-side sanitization.
 *
 * Baqaal sanitizes and freezes a snapshot per version, so this should never
 * have anything to strip. It exists so that a regression there cannot become
 * stored XSS in the partner portal, and so the `dangerouslySetInnerHTML` call
 * downstream is defensible on its own terms.
 */
export const sanitizeTermsHtml = (html: string): string =>
	DOMPurify.sanitize(html, {
		ALLOWED_TAGS,
		ALLOWED_ATTR,
		// Anchors are the only thing carrying a URL; keep the same scheme
		// restriction the server applies.
		ALLOWED_URI_REGEXP: /^(?:https?:\/\/|mailto:)/i,
		/*
		 * Any attribute DOMPurify does not already know to be URI-free has its
		 * *value* tested against ALLOWED_URI_REGEXP — so under the scheme
		 * restriction above, `start="13"` is dropped as a URL that is neither
		 * http(s) nor mailto. Declaring it URI-safe skips that test; allowlisting
		 * it is not enough on its own.
		 */
		ADD_URI_SAFE_ATTR: ["start"],
	});

/** Looks like a tag: `<p`, `</p`, `<br>`. Deliberately narrow. */
const LOOKS_LIKE_MARKUP = /<\/?[a-z][a-z0-9]*[\s/>]/i;

/**
 * True when the body's *text* is itself HTML — i.e. the markup was entity-
 * encoded before it reached us, so `&lt;p&gt;` arrives where `<p>` was meant.
 */
const isEntityEncodedMarkup = (html: string): boolean => {
	const text = new DOMParser().parseFromString(html, "text/html").body.textContent ?? "";
	return LOOKS_LIKE_MARKUP.test(text);
};

/**
 * Turns the API's `html` field into markup the viewer can display.
 *
 * ## The escaped-content shim
 *
 * The v1.1 CMS entry was authored with its markup entity-encoded and wrapped in
 * a single `<p>` with `<br>` line breaks, so the document arrives as a wall of
 * literal `&lt;p&gt;` text and renders as source code rather than a contract.
 *
 * **This is a data problem, not a rendering one, and the real fix is upstream**
 * — the CMS body should hold actual markup. Two consequences worth knowing:
 *
 * 1. Baqaal's sanitizer only ever saw escaped *text*, which passes any allowlist
 *    trivially. The server-side guarantee is therefore not in force for this
 *    document; the client-side pass below is doing the real sanitization.
 * 2. The encoded body uses tags outside the agreed allowlist (`<mark>`,
 *    `ol type="1"`, `colgroup`). Those are dropped here, so highlights and
 *    list-numbering styles will not survive even once decoding works.
 *
 * The shim decodes once and re-sanitizes with the same strict allowlist, so
 * nothing renders that would not have rendered from a well-formed document. It
 * is a no-op for correctly authored content, and should be deleted once the CMS
 * entry is fixed.
 */
export const prepareTermsHtml = (html: string): string => {
	if (!html) {
		return "";
	}

	if (!isEntityEncodedMarkup(html)) {
		return sanitizeTermsHtml(html);
	}

	// `textContent` performs exactly one round of entity decoding — never more,
	// so a doubly-encoded payload cannot be unwrapped into live markup.
	const decoded = new DOMParser().parseFromString(html, "text/html").body.textContent ?? "";

	return sanitizeTermsHtml(decoded);
};
